import {
  ExtendedFilter,
  Filter,
  FilterObject,
} from '@insurancemenu/imenu-components-lib/dist/types/filterSidebarTypings';
import {
  BrowsingProduct,
  Portfolio,
  Product,
  ProductLineAttr,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/src/types/ProductLineId';
import { HELP_ME_DECIDE_HELPER, INITIAL_DDWI_BC } from 'global/utils/HelpMeDecideHelper';
import { makeAutoObservable, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';

export const INITIAL_FILTER = { rates: [], tag: [] };

export class BrowsingFilterStore {
  rootStore: RootStore;
  /**
   * It is used for saving filters in help me decide flow
   */
  filters: ExtendedFilter = INITIAL_FILTER;
  /**
   * It is used for saving browsing criteria in help me decide flow
   */
  browsingCriteria: Record<string, string> = {};

  // Help me decide methods only - Start
  public setFilters(filters: ExtendedFilter): void {
    this.filters = filters;
  }

  public setBrowsingCriteria(bc: Record<string, string>): void {
    this.browsingCriteria = bc;
  }

  // Remove Actions
  private removeFilterValue(filters: ExtendedFilter, filterName: string): void {
    if (filters[filterName]) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [filterName as keyof ExtendedFilter]: _, ...restFilters } = filters;
      this.setFilters(restFilters as ExtendedFilter);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  public removeFilterFromNextCriteria(
    sponsorId: string,
    currentCriteriaIndex: number,
    productLineId: ProductLineId,
  ): void {
    const filters = toJS(this.filters);
    const removedFilterName = HELP_ME_DECIDE_HELPER[sponsorId]?.dynamicNames[productLineId]?.[currentCriteriaIndex];
    if (removedFilterName) this.removeFilterValue(filters, removedFilterName);
  }

  // eslint-disable-next-line class-methods-use-this
  public resetBcFromNextCriteria(sponsorId: string, currentCriteriaIndex: number, productLineId: ProductLineId): void {
    const resetedBcName = HELP_ME_DECIDE_HELPER[sponsorId]?.dynamicNames[productLineId]?.[currentCriteriaIndex];
    if (resetedBcName) {
      const initialBcValue = INITIAL_DDWI_BC[resetedBcName] || '';
      this.onBrowsingCriteriaChange(resetedBcName, initialBcValue);
    }
  }

  // Get Actions
  // eslint-disable-next-line class-methods-use-this
  public getFilterRangeValue(filterName: string): string {
    const filters = toJS(this.filters);
    // Check if filters[filterName] is an array and convert its first element to a string if it exists
    // It should be string for component state
    return Array.isArray(filters[filterName]) && (filters[filterName] as unknown[]).length > 0
      ? String((filters[filterName] as unknown[])[0])
      : 'all';
  }

  public getFilterRangeValueDownUp(filterName: string): string {
    const filters = toJS(this.filters);
    return Array.isArray(filters[filterName]) && (filters[filterName] as unknown[]).length === 2
      ? `${String((filters[filterName] as unknown[])[0])}-${String((filters[filterName] as unknown[])[1])}`
      : '';
  }

  // eslint-disable-next-line class-methods-use-this
  public getFilterBooleanCheckboxValue(filterName: string, filterValue: string): string {
    const filters = toJS(this.filters);
    // Check if checkbox value is true , return string value for radio button
    // It should be string for component state
    return filters[filterName] ? filterValue : 'all';
  }

  // eslint-disable-next-line class-methods-use-this
  public getFilterValueCheckboxValue(filterName: string): string {
    const filters = toJS(this.filters);
    // Check if checkbox value is in the array, return this value for radio button
    // It should be string for component state
    return filters[filterName] && (filters[filterName] as unknown[]).length > 0
      ? String((filters[filterName] as unknown[])[0])
      : 'all';
  }

  public getBrowsingCriteriaValue(name: string): string {
    return this.browsingCriteria[name] || '';
  }

  // Change actions
  public onRangeFilterChange(filterName: string, filterValue: string): void {
    const filters = toJS(this.filters);
    if (filterValue === 'all') {
      this.removeFilterValue(filters, filterName);
    } else {
      this.setFilters({ ...filters, [filterName]: [+filterValue, +filterValue] });
    }
  }

  // Change actions
  public onRangeFilterChangeDownUp(filterName: string, filterValue: string): void {
    const filters = toJS(this.filters);
    const [filterValueDown, filterValueUp] = filterValue.split('-');
    this.setFilters({ ...filters, [filterName]: [+filterValueDown, +filterValueUp] });
  }

  public onBooleanCheckboxFilterChange(filterName: string, filterValue: string): void {
    const filters = toJS(this.filters);
    if (filterValue === 'all') {
      this.removeFilterValue(filters, filterName);
    } else {
      this.setFilters({ ...filters, [filterName]: true });
    }
  }

  public onValueCheckboxFilterChange(filterName: string, filterValue: string): void {
    const filters = toJS(this.filters);
    if (filterValue === 'all') {
      this.removeFilterValue(filters, filterName);
    } else {
      this.setFilters({ ...filters, [filterName]: [filterValue] });
    }
  }

  public onBrowsingCriteriaChange(name: string, value: string): void {
    const bc = toJS(this.browsingCriteria);
    bc[name] = value;
    this.setBrowsingCriteria(bc);
  }

  public onCarrierFilterChange(filterName: string, filterValue: string): void {
    const filters = toJS(this.filters);
    if (filterValue === 'all') {
      this.removeFilterValue(filters, filterName);
    } else {
      this.setFilters({ ...filters, [filterName]: filterValue });
    }
  }

  // eslint-disable-next-line class-methods-use-this
  public areFiltersSet(filters: ExtendedFilter | Filter): boolean {
    if (Object.keys(filters).length > 2) {
      return true;
    }
    if (filters.rates.length === 0 && filters.tag.length === 0) {
      return false;
    }
    return true;
  }

  // Help me decide methods only - End

  public filterPortfoliosBy(list: Portfolio[], filters: Filter, attrs: ProductLineAttr[]): Portfolio[] {
    let updatedPortfolios = [...list];
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'tag') {
        updatedPortfolios = this.filterByTag(value as string[], updatedPortfolios);
      }
      if (key === 'rates') {
        updatedPortfolios = this.filterByRates(value as FilterObject[], updatedPortfolios);
      }
      if (key === 'carrier') {
        updatedPortfolios = this.filterByCarrier(value as string[], updatedPortfolios);
      }
      if (key !== 'rates' && key !== 'tag' && key !== 'carrier') {
        updatedPortfolios = this.filterByType(key, value, updatedPortfolios, attrs);
      }
    });
    return updatedPortfolios;
  }

  // Show portfolio if it has one of tag which is selected in filter
  // eslint-disable-next-line class-methods-use-this
  private filterByTag(tags: string[], list: Portfolio[]): Portfolio[] {
    if (!tags.length || !list.length) return list;

    return list.filter((portfolio) => {
      const hasOneOfTag = tags.some((tagId) => portfolio.marketingTagIds.includes(tagId));

      if (hasOneOfTag) return portfolio;
      return null;
    });
  }

  // Show portfolio if it has one of carrier which is selected in filter
  // eslint-disable-next-line class-methods-use-this
  private filterByCarrier(carrierIds: string[], portfolios: Portfolio[]): Portfolio[] {
    if (!carrierIds.length || !portfolios.length) return portfolios;
    return portfolios.filter((portfolio) => {
      if (portfolio.products.some(({ carrier }) => carrierIds.includes(carrier.id))) {
        return portfolio;
      }
      return null;
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private filterByRates(filterRates: FilterObject[], list: Portfolio[]): Portfolio[] {
    if (!filterRates.length || !list.length) return list;
    const filteredList: Portfolio[] = [];

    list.forEach((portfolio) => {
      // search in products list
      const filteredProducts = portfolio.products.filter((product) => {
        // get only rates than fit the filter
        const ratesInRange = product.bcDependentData[0].rates.filter(({ displayKey, value }) => {
          const currentFilterRateObj = filterRates.find((rate) => rate[displayKey]);

          if (currentFilterRateObj) {
            const rateValues = currentFilterRateObj[displayKey];

            if (+value >= rateValues[0] && +value <= rateValues[1]) {
              return { displayKey, value };
            }
          }
          // currentFilterRateObj can be undefined if rate with this displayKey is not selected in filter
          return null;
        });

        if (
          ratesInRange.length === filterRates.length ||
          ratesInRange.length === product.bcDependentData[0].rates.length // Product should not be filtered out, if it has different amount of rates from the filters, but still all of them pass
        ) {
          return product;
        }
        return null;
      });

      // hide portfolio if all products are inactive or highlight inactive in multi product portfolio
      const updatedPortfolio = this.filterProducts(portfolio, filteredProducts);
      if (updatedPortfolio) {
        filteredList.push(updatedPortfolio);
      }
    });

    return filteredList;
  }

  private filterByType<T>(
    displayName: string,
    filterValue: T, // possible variants string[] | number[] | boolean,
    list: Portfolio[],
    productLineAttrs: ProductLineAttr[],
  ): Portfolio[] {
    const currentAttr = productLineAttrs.find((attr) => attr.displayName === displayName); // attr whole object
    const type = currentAttr?.type;

    return list.filter((portfolio) => {
      // search in products list
      const filteredProducts = portfolio.products.filter((product) => {
        // find attribute in product object that was changed in filter
        const currentProductAttr = product.bcDependentData[0].productAttributes.find(
          (productAttr) => productAttr.attributeId === currentAttr?.id,
        );

        if (!currentProductAttr) return null;

        let isValueFitTheFilter;
        if (type === 'booleanCheckbox' && typeof filterValue === 'boolean') {
          isValueFitTheFilter = filterValue.toString() === currentProductAttr.attributeValue;
        }
        if (type === 'valueCheckbox' && Array.isArray(filterValue)) {
          isValueFitTheFilter =
            filterValue.length === 0 ? true : filterValue.includes(currentProductAttr.attributeValue || '');
        }
        if (type === 'rangeValue' && Array.isArray(filterValue)) {
          isValueFitTheFilter = this.checkIsValueInRange(currentProductAttr.attributeValue || '', filterValue);
        }
        if (isValueFitTheFilter) return product;
        return null;
      });

      // highlight inactive products in multi product portfolio or
      // hide portfolio if all products are inactive
      return this.filterProducts(portfolio, filteredProducts);
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private checkIsValueInRange(attributeValue: string, filterRange: number[]): boolean {
    return +attributeValue >= filterRange[0] && +attributeValue <= filterRange[1];
  }

  // for multi product portfolio
  // eslint-disable-next-line class-methods-use-this
  private highlightInactiveProducts(portfolio: Portfolio, filteredProducts: Product[]): Portfolio | null {
    const allProductIds = portfolio.products.map(({ imProductId }) => imProductId);
    const activeProductIds = filteredProducts.map(({ imProductId }) => imProductId);

    const updatedProducts = portfolio.products.map((product): BrowsingProduct => {
      if (activeProductIds.includes(product.imProductId)) {
        return { ...product, disabled: false };
      }
      return { ...product, disabled: true };
    });

    const isEveryProductDisabled = updatedProducts.every(({ disabled }) => disabled);

    if (activeProductIds.length !== allProductIds.length && !isEveryProductDisabled) {
      return { ...portfolio, products: updatedProducts };
    }

    return null;
  }

  private filterProducts(portfolio: Portfolio, filteredProducts: Product[]): Portfolio | null {
    // highlight inactive products in multi product portfolio
    if (portfolio.products.length > 1 && portfolio.products.length !== filteredProducts.length) {
      const updatedPortfolio = this.highlightInactiveProducts(portfolio, filteredProducts);
      if (updatedPortfolio) return updatedPortfolio;
      return null;
    }

    return portfolio.products.length === filteredProducts.length ? portfolio : null;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
